/**
 * Chat API Contract
 *
 * Mirrors: backend/src/routes/chat.routes.ts
 * 1:1 parity with existing Express routes
 */
import { oc } from "@orpc/contract";
import { z } from "zod";
import { CreateConversationInputSchema, SendMessageInputSchema, GetMessagesQuerySchema, GetConversationsQuerySchema, GetTeamMembersQuerySchema, MessageResponseSchema, ConversationResponseSchema, ConversationListResponseSchema, MessageListResponseSchema, TeamMemberResponseSchema, ChatUnreadCountResponseSchema, MarkAsReadResponseSchema, } from "@hourly/api/chat";
import { uuidSchema } from "@hourly/api/validation-common";
// ============================================
// Conversation Contracts
// ============================================
/**
 * GET /api/conversations
 * List user's conversations
 */
export const getConversations = oc
    .route({
    method: "GET",
    path: "/conversations",
    summary: "List user's conversations",
    tags: ["Chat"],
})
    .input(GetConversationsQuerySchema)
    .output(ConversationListResponseSchema);
/**
 * GET /api/conversations/unread-count
 * Get total unread message count for user
 */
export const getUnreadCount = oc
    .route({
    method: "GET",
    path: "/conversations/unread-count",
    summary: "Get total unread message count",
    tags: ["Chat"],
})
    .input(z.object({}))
    .output(ChatUnreadCountResponseSchema);
/**
 * GET /api/conversations/team-members
 * List team members available to chat with
 */
export const getTeamMembers = oc
    .route({
    method: "GET",
    path: "/conversations/team-members",
    summary: "List team members available to chat with",
    tags: ["Chat"],
})
    .input(GetTeamMembersQuerySchema)
    .output(z.array(TeamMemberResponseSchema));
/**
 * POST /api/conversations
 * Create new conversation
 */
export const createConversation = oc
    .route({
    method: "POST",
    path: "/conversations",
    summary: "Create new conversation",
    tags: ["Chat"],
    successStatus: 201,
})
    .input(CreateConversationInputSchema)
    .output(ConversationResponseSchema);
/**
 * GET /api/conversations/:id
 * Get a specific conversation
 */
export const getConversation = oc
    .route({
    method: "GET",
    path: "/conversations/{id}",
    summary: "Get conversation by ID",
    tags: ["Chat"],
})
    .input(z.object({ id: uuidSchema }))
    .output(ConversationResponseSchema);
/**
 * GET /api/conversations/:id/messages
 * Get messages for a conversation (paginated)
 */
export const getMessages = oc
    .route({
    method: "GET",
    path: "/conversations/{id}/messages",
    summary: "Get messages for a conversation",
    tags: ["Chat"],
})
    .input(z.object({ id: uuidSchema }).merge(GetMessagesQuerySchema))
    .output(MessageListResponseSchema);
/**
 * POST /api/conversations/:id/messages
 * Send a message
 */
export const sendMessage = oc
    .route({
    method: "POST",
    path: "/conversations/{id}/messages",
    summary: "Send a message in a conversation",
    tags: ["Chat"],
    successStatus: 201,
})
    .input(z.object({ id: uuidSchema }).merge(SendMessageInputSchema.omit({ conversationId: true })))
    .output(MessageResponseSchema);
/**
 * POST /api/conversations/:id/read
 * Mark conversation as read
 */
export const markAsRead = oc
    .route({
    method: "POST",
    path: "/conversations/{id}/read",
    summary: "Mark conversation as read",
    tags: ["Chat"],
})
    .input(z.object({ id: uuidSchema }))
    .output(MarkAsReadResponseSchema);
// ============================================
// Contract Router
// ============================================
export const chatContract = {
    getConversations,
    getUnreadCount,
    getTeamMembers,
    createConversation,
    getConversation,
    getMessages,
    sendMessage,
    markAsRead,
};
//# sourceMappingURL=chat.js.map